/* Verolae · Video Carousel
   Port of the inline <script> of the source site section `mb_video_carousel_jWVffd` ("Real People. Real Results."):
   - measures the track: when all 9:16 cards fit inside the (max 1200px) wrap the track is centred and arrows + dots are
     hidden; otherwise a Swiper instance is created on the wrap (custom wrapperClass / slideClass, 3.5 slides per view
     below 768px, 'auto' from 768px, 6 from 1024px, grab cursor, prev / next arrows, clickable bullet pagination rendered
     as <button>s); re-evaluated on window resize and on theme-editor section load;
   - click on a card: stops any other card, hides poster / overlay / play badge (.vc--playing) and injects an unmuted,
     looping, inline <video> (or an <iframe> for YouTube / Vimeo URLs) with custom controls: a centre play / pause overlay
     that fades out 800ms after playback starts and a bottom-right mute toggle. If the unmuted autoplay is blocked the
     video is restarted muted. Clicking the active card toggles play / pause; a slide change stops playback.
   Additions over the source: one instance per section (data-section-id) instead of document-wide ids, support for the
   Shopify video picker (a <template class="vc__video-tpl"> rendered by video_tag is cloned instead of building
   <video src>), keyboard activation of the cards (Enter / Space), a 150ms resize debounce, and theme-editor
   load / unload / block-select handling.
   Uses the globally loaded Swiper 11 (window.Swiper, theme.liquid). No other dependencies. */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.verolae-video-carousel[data-section-id]';
  var GAP = 12; // must match .vc__track { gap } in the stylesheet (source: var gap = 12)
  var instances = {};

  var CONTROLS_HTML =
    '<div class="vc__inline-play-overlay">' +
      '<svg class="vc__inline-play-icon" viewBox="0 0 24 24" style="display:none" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg>' +
      '<svg class="vc__inline-pause-icon" viewBox="0 0 24 24" style="display:none" aria-hidden="true" focusable="false"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>' +
    '</div>' +
    '<button type="button" class="vc__inline-mute-btn" aria-label="Toggle sound">' +
      '<svg class="vc__inline-mute-icon" viewBox="0 0 24 24" style="display:none" aria-hidden="true" focusable="false"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>' +
      '<svg class="vc__inline-unmute-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>' +
    '</button>';

  /* Source helper, unchanged: YouTube / Vimeo page URLs -> autoplaying embed URLs. */
  function getEmbedUrl(url, type) {
    if (!url) return '';
    if (type === 'youtube') {
      var m = url.match(/(?:youtu\.be\/|v=)([^&?\/]+)/);
      return m ? 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1&rel=0' : url;
    }
    if (type === 'vimeo') {
      var m2 = url.match(/vimeo\.com\/(\d+)/);
      return m2 ? 'https://player.vimeo.com/video/' + m2[1] + '?autoplay=1' : url;
    }
    return url;
  }

  function whenSwiperReady(callback, onTimeout) {
    var tries = 0;
    (function poll() {
      if (window.Swiper) { callback(); return; }
      tries += 1;
      if (tries > 100) { if (onTimeout) onTimeout(); return; } // ~10 s; Swiper is loaded globally in theme.liquid
      setTimeout(poll, 100);
    })();
  }

  function setDisplay(el, value) {
    if (el) el.style.display = value;
  }

  function create(root) {
    var sid = root.getAttribute('data-section-id');
    if (!sid || instances[sid]) return;

    /* ── Elements ── */
    var track    = root.querySelector('.vc__track');
    var wrap     = root.querySelector('.vc__track-wrap');
    var dotsWrap = root.querySelector('.vc__dots');
    var prevBtn  = root.querySelector('.vc__arrow--prev');
    var nextBtn  = root.querySelector('.vc__arrow--next');

    if (!track || !wrap) return;

    var cards      = Array.prototype.slice.call(track.querySelectorAll('.vc__card'));
    var totalCards = cards.length;
    if (!totalCards) return;

    var activeCard        = null;
    var activeToggleIcon  = null; // showPlayPauseIcon() of the card currently playing
    var swiperInstance    = null;
    var resizeTimer       = null;
    var waitingForSwiper  = false;
    var destroyed         = false;

    /* ── Swiper init/destroy logic based on overflow (source order: measure, destroy, decide) ── */
    function initOrDestroySwiper() {
      if (destroyed) return;

      var cardW = cards[0].offsetWidth + GAP;
      var wrapW = wrap.offsetWidth;
      var totalTrackW = totalCards * cardW - GAP;

      if (swiperInstance) {
        swiperInstance.destroy(true, true);
        swiperInstance = null;
      }

      if (totalTrackW <= wrapW) {
        track.style.justifyContent = 'center';
        setDisplay(prevBtn, 'none');
        setDisplay(nextBtn, 'none');
        setDisplay(dotsWrap, 'none');
        return;
      }

      track.style.justifyContent = 'flex-start';
      setDisplay(prevBtn, 'flex');
      setDisplay(nextBtn, 'flex');
      setDisplay(dotsWrap, 'flex');

      if (!window.Swiper) {
        // Swiper is a deferred global script; retry as soon as it is available.
        if (!waitingForSwiper) {
          waitingForSwiper = true;
          whenSwiperReady(
            function () { waitingForSwiper = false; initOrDestroySwiper(); },
            function () {
              // Graceful fallback (not in the source): no Swiper -> plain horizontal scrolling, no controls.
              waitingForSwiper = false;
              if (destroyed) return;
              wrap.style.overflowX = 'auto';
              setDisplay(prevBtn, 'none');
              setDisplay(nextBtn, 'none');
              setDisplay(dotsWrap, 'none');
            }
          );
        }
        return;
      }

      swiperInstance = new window.Swiper(wrap, {
        wrapperClass: 'vc__track',
        slideClass: 'vc__card',
        slidesPerView: 3.5,
        spaceBetween: 3,
        grabCursor: true,
        watchSlidesProgress: true,
        breakpoints: {
          768: {
            slidesPerView: 'auto',
            spaceBetween: GAP
          },
          1024: {
            slidesPerView: 6
          }
        },
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn
        },
        pagination: {
          el: dotsWrap,
          clickable: true,
          bulletClass: 'vc__dot',
          bulletActiveClass: 'vc--active',
          renderBullet: function (index, className) {
            return '<button type="button" class="' + className + '" aria-label="Slide ' + (index + 1) + '"></button>';
          }
        },
        on: {
          slideChangeTransitionStart: function () {
            stopAllVideos();
          }
        }
      });
    }

    /* ── Inline Video Player ── */
    function stopAllVideos() {
      if (!activeCard) return;
      activeCard.classList.remove('vc--playing');
      var oldVideoOrIframe = activeCard.querySelector('.vc__inline-video');
      if (oldVideoOrIframe) {
        if (oldVideoOrIframe.tagName === 'VIDEO') {
          try { oldVideoOrIframe.pause(); } catch (e) { /* noop */ }
        }
        oldVideoOrIframe.remove();
      }
      var oldControls = activeCard.querySelector('.vc__inline-controls');
      if (oldControls) oldControls.remove();
      activeCard = null;
      activeToggleIcon = null;
    }

    function playVideoInline(card) {
      var url  = card.getAttribute('data-video-url') || '';
      var type = card.getAttribute('data-video-type') || 'youtube';
      var tpl  = card.querySelector('template.vc__video-tpl');
      var tplVideo = tpl && tpl.content ? tpl.content.querySelector('video') : null;
      if (!url && !tplVideo) return;

      if (activeCard === card) {
        // Second click on the playing card: toggle play / pause.
        var current = card.querySelector('video.vc__inline-video');
        if (current) {
          if (current.paused) {
            var resumed = current.play();
            if (resumed && typeof resumed.catch === 'function') resumed.catch(function () { /* noop */ });
            if (activeToggleIcon) activeToggleIcon(false);
          } else {
            current.pause();
            if (activeToggleIcon) activeToggleIcon(true);
          }
        }
        return;
      }

      stopAllVideos();

      activeCard = card;
      card.classList.add('vc--playing');

      var inner = card.querySelector('.vc__card-inner');
      var isDirectVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(url);
      var embed;

      if (tplVideo || isDirectVideo) {
        if (tplVideo) {
          // Shopify-hosted video chosen in the theme editor: <video> (with <source>s) rendered by video_tag.
          embed = tplVideo.cloneNode(true);
          embed.removeAttribute('controls');
          embed.removeAttribute('autoplay');
        } else {
          embed = document.createElement('video');
          embed.src = url;
        }
        embed.className   = 'vc__inline-video';
        embed.controls    = false;
        embed.autoplay    = true;
        embed.loop        = true;
        embed.muted       = false;
        embed.playsInline = true;
        embed.setAttribute('playsinline', '');

        var controls = document.createElement('div');
        controls.className = 'vc__inline-controls';
        controls.innerHTML = CONTROLS_HTML;

        var playOverlay = controls.querySelector('.vc__inline-play-overlay');
        var muteBtn     = controls.querySelector('.vc__inline-mute-btn');
        var playIcon    = controls.querySelector('.vc__inline-play-icon');
        var pauseIcon   = controls.querySelector('.vc__inline-pause-icon');
        var muteIcon    = controls.querySelector('.vc__inline-mute-icon');
        var unmuteIcon  = controls.querySelector('.vc__inline-unmute-icon');

        var updateMuteUI = function () {
          if (embed.muted) {
            muteIcon.style.display = 'block';
            unmuteIcon.style.display = 'none';
          } else {
            muteIcon.style.display = 'none';
            unmuteIcon.style.display = 'block';
          }
        };

        muteBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          embed.muted = !embed.muted;
          updateMuteUI();
        });

        var fadeTimeout;
        var showPlayPauseIcon = function (isPaused) {
          clearTimeout(fadeTimeout);
          if (isPaused) {
            playIcon.style.display = 'block';
            pauseIcon.style.display = 'none';
            playOverlay.style.opacity = '1';
          } else {
            playIcon.style.display = 'none';
            pauseIcon.style.display = 'block';
            playOverlay.style.opacity = '1';
            fadeTimeout = setTimeout(function () {
              playOverlay.style.opacity = '0';
            }, 800);
          }
        };
        activeToggleIcon = showPlayPauseIcon;

        embed.addEventListener('playing', function () {
          showPlayPauseIcon(false);
        });

        inner.appendChild(embed);
        inner.appendChild(controls);

        var attempt = embed.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(function () {
            // Unmuted autoplay blocked by the browser: restart muted (source behaviour).
            embed.muted = true;
            updateMuteUI();
            var retry = embed.play();
            if (retry && typeof retry.catch === 'function') retry.catch(function () { /* noop */ });
          });
        }
      } else {
        embed = document.createElement('iframe');
        embed.className       = 'vc__inline-video';
        embed.src             = getEmbedUrl(url, type);
        embed.allow           = 'autoplay; fullscreen';
        embed.allowFullscreen = true;
        inner.appendChild(embed);
      }
    }

    function onCardClick() {
      playVideoInline(this);
    }

    function onCardKeydown(e) {
      if (e.target !== this) return; // let the inline mute <button> keep its own keyboard behaviour
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        playVideoInline(this);
      }
    }

    cards.forEach(function (card) {
      card.addEventListener('click', onCardClick);
      card.addEventListener('keydown', onCardKeydown);
    });

    /* ── Init + resize ── */
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(initOrDestroySwiper, 150);
    }
    window.addEventListener('resize', onResize);

    instances[sid] = {
      refresh: initOrDestroySwiper,
      goToSlide: function (index) {
        if (swiperInstance) swiperInstance.slideTo(index);
      },
      destroy: function () {
        destroyed = true;
        window.removeEventListener('resize', onResize);
        clearTimeout(resizeTimer);
        stopAllVideos();
        if (swiperInstance) {
          swiperInstance.destroy(true, true);
          swiperInstance = null;
        }
        cards.forEach(function (card) {
          card.removeEventListener('click', onCardClick);
          card.removeEventListener('keydown', onCardKeydown);
        });
        delete instances[sid];
      }
    };

    initOrDestroySwiper();
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(create);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify theme editor */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var root = event.target.querySelector(ROOT_SELECTOR);
      if (!root) return;
      var id = root.getAttribute('data-section-id');
      if (instances[id]) instances[id].destroy();
      create(root);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      var id = event.detail && event.detail.sectionId;
      if (id && instances[id]) instances[id].destroy();
    });
    document.addEventListener('shopify:block:select', function (event) {
      var card = event.target.closest('.vc__card');
      var root = event.target.closest(ROOT_SELECTOR);
      if (!card || !root) return;
      var inst = instances[root.getAttribute('data-section-id')];
      var index = parseInt(card.getAttribute('data-slide-index'), 10);
      if (inst && !isNaN(index)) inst.goToSlide(index);
    });
  }
})();
